import { FunctionComponent, useState, useEffect, useRef, useMemo } from 'react';
import { Typography, Button, Dropdown, InlineMessage, DateRangePicker, ProgressBar, Spinner } from "@volvo/vcdk-react";
import { DateCard } from './InvoiceTab/DateCard';
import { ProductCard } from './InvoiceTab/ProductCard';
import { CurrencyDropdown } from './InvoiceTab/CurrencyDropdown';
import { DealerCard } from './InvoiceTab/DealerCard';
import { InvoiceTable } from './InvoiceTab/InvoiceTable';
import { useLazyPrepareQuery, useGenerateExcelMutation, 
    useLazySummarySheetQuery, useLazyMarketSheetQuery, useDealerSheetMutation,
    useRequestExcelMutation, useGetDealerInvoiceMutation } from '../store/InvoiceApiSlice';
import { InvoicePayload, Product, Application, Dealer, Invoice, DealerClass, AccountableType } from '../Types';

export const InvoiceTab: FunctionComponent = () => {

    const [invoiceList, setInvoiceList] = useState<Invoice[]>([]);
    const [progress, setProgress] = useState<{total: number | undefined, current: number}>({total: undefined, current: 0});
    
    const refDateCard = useRef<any>(null), refProductCard = useRef<any>(null), refDealerCard = useRef<any>(null), currencyDropdownRef = useRef<any>(null);
    const refIBCIdArray = useRef<number[]>([]);
    const refExcelStatus = useRef<{
        summary: {data: string, yes:boolean}, market: {data: string, yes:boolean}, dealer: {data: string, yes:boolean}
    }>({summary: {data: '', yes: false}, market: {data: '', yes: false}, dealer: {data: '', yes: false}});
    const [btnSearch, setBtnSearch] = useState<{loading: boolean, disabled: boolean}>({loading: false, disabled: false});
    const [btnExcel, setBtnExcel] = useState<{loading: boolean, disabled: boolean}>({loading: false, disabled: true});
    const [prepareMsg, setPrepareMsg] = useState<string | undefined>(undefined);
    const [prepareData, {}] = useLazyPrepareQuery();
    const [generateExcel, {}] = useGenerateExcelMutation();
    const [getDealerInvoice, { isUninitialized, isLoading: isInvoiceLoading, data: invoice, isSuccess: isInvoiceSuccess, isError: isInvoiceError, error: invoiceError }] = useGetDealerInvoiceMutation();
    const [requestExcel, {isLoading}] = useRequestExcelMutation();
    const [querySummarySheet, {}] = useLazySummarySheetQuery();
    const [queryMarketSheet, {}] = useLazyMarketSheetQuery();
    const [queryDealerSheet, {}] = useDealerSheetMutation();
    const refPayload = useRef<InvoicePayload>({
        countryId: 0,
        dealerIdArray: "",
        startDate: undefined,
        endDate: undefined,
        productArray: "",
        applicationArray: "",
        currencyId: 0,
        contractArray: [],
        omittedClientIds: [],
        omittedIBCIds: [],
    });
    const queueRequest = useRef<Dealer[]>([]), refActiveRequest = useRef<number>(0);
    const [requestList, setRequestList] = useState<{key: number}[]>([]);
    const [errorList, setErrorList] = useState<string[]>([]);
    const MAX_CONCURRENT_REQUESTS = 5, MAX_BATCH = 20;

    const startQueue = () => {
        while (refActiveRequest.current < MAX_CONCURRENT_REQUESTS && queueRequest.current.length > 0) {
            const dealerIdArray = queueRequest.current.splice(0, MAX_BATCH);
            refActiveRequest.current = refActiveRequest.current + 1;
            setRequestList(prev => ([...prev, { key: refActiveRequest.current }]));
            getDealerInvoice({...refPayload.current, dealerIdArray: dealerIdArray.map(dealer => dealer.dealerID).join(',')}).unwrap().then((res: Invoice[]) => {
                for(let invoice of res) refIBCIdArray.current = [...refIBCIdArray.current, ...invoice.itemList.map((item: any) => item.invoiceBasisCalculationID)];
                setInvoiceList(prev => [...prev, ...res].sort((a, b) => parseInt(a.dealerNo, 10) - parseInt(b.dealerNo, 10)));
                setProgress(prevState => {
                    let current = prevState.current + dealerIdArray.length;
                    if(current === prevState.total){
                        setBtnSearch({ loading: false, disabled: false });
                        setBtnExcel(prev => ({ ...prev, disabled: false }));
                    } 
                    return { ...prevState, current }
                });
            }).catch(err => {                                
                setErrorList(prev => {
                    if(prev.includes(err.error)) return prev;
                    return [...prev, err.error];
                });
            }).finally(() => {
                setRequestList(prev => {
                    let [, ...rest] = prev;
                    return rest;
                });
                refActiveRequest.current = refActiveRequest.current - 1;
                startQueue();
            });
        }
    }

    const summarySheet = async (generationOccasionId: number) => {
        querySummarySheet({generationOccasionId}).unwrap().then((res) => {
            if(res.errMsg == 'OK') {
                refExcelStatus.current.summary = {data: res.data, yes: true};
                setProgress(prev => ({ ...prev, current: prev.current + 25 }));
                setPrepareMsg(prev => `${prev} Summary sheet done.`);
                downloadExcel();
            }else{
                setPrepareMsg(prev => `${prev}. ${res.errMsg}.`);
            }
        });
    }
    const marketSheet = async (generationOccasionId: number) => {
        queryMarketSheet({generationOccasionId}).unwrap().then((res) => {
            if(res.errMsg == 'OK') {
                //refExcelStatus.current.market = {data: res.data, yes: true};
                setProgress(prev => ({ ...prev, current: prev.current + 25 }));
                setPrepareMsg(prev => `${prev} Market sheet done.`);
                queryDealerSheet({marketData: res.data}).unwrap().then((res) => {
                    console.log(res);
                    if(res.errMsg == 'OK') {
                        refExcelStatus.current.market = {data: res.marketData, yes: true};
                        refExcelStatus.current.dealer = {data: res.dealerData, yes: true};
                        setProgress(prev => ({ ...prev, current: prev.current + 25 }));
                        setPrepareMsg(prev => `${prev} Dealer sheet done.`);
                        downloadExcel();
                    }
                });                
            }else{
                setPrepareMsg(prev => `${prev}. ${res.errMsg}.`);
            }
        });        
    }
    const dealerSheet = async (generationOccasionId: number) => {
        downloadExcel();
    }

    const downloadExcel = async () => {
        if(refExcelStatus.current.summary.yes && refExcelStatus.current.market.yes && refExcelStatus.current.dealer.yes) {
            const blob = await requestExcel({summaryData: refExcelStatus.current.summary.data, marketData: refExcelStatus.current.market.data, dealerData: refExcelStatus.current.dealer.data}).unwrap();
            const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
            const link = document.createElement('a');
            link.href = url;
            const selectedCountry = refDealerCard.current.getSelectedCountry();
            const date = new Date().toISOString().split('T')[0];
            link.setAttribute('download', `${date}-${selectedCountry.name}.xlsx`);
            link.click();
            window.URL.revokeObjectURL(url);
            setProgress(prev => ({ ...prev, current: prev.current + 5 }));
            setPrepareMsg(prev => `${prev} Excel file downloaded.`);
            setBtnSearch(prev => ({ ...prev, disabled: false }));
            setBtnExcel(prev => ({ ...prev, disabled: false, loading: false }));
        }
    }

    const onSubmit = () => {
        const {startDate, endDate} = refDateCard.current.getDateRange();
        const productFilter = refProductCard.current.getSelectedProducts();
        const selectedDealers = refDealerCard.current.getSelectedDealers();
        const contractArray = refDealerCard.current.getSelectedContracts();
        const currencyId = currencyDropdownRef.current.getCurrency();
        const selectedCountry = refDealerCard.current.getSelectedCountry();
        if(!startDate || !endDate || productFilter.selectedProducts.length === 0 || selectedDealers.length === 0 || contractArray.length === 0) {
            if(selectedDealers.length === 0){
                setInvoiceList([]);
                setProgress(prev => ({ ...prev, total: undefined, current: 0 }));
            }
            return;
        }
        setInvoiceList([]);
        setBtnSearch({ loading: true, disabled: true });
        setBtnExcel(prev => ({ ...prev, disabled: true }));
        setProgress({ total: undefined, current: 0 });
        setPrepareMsg('Prepareing application costs data...');
        prepareData({startDate, endDate, countryId: selectedCountry.id, contractArray: contractArray.length == 0 ? '' : contractArray.join(',')}).unwrap().then(res => {
            if(res.errMsg == 'OK') {
                setPrepareMsg(undefined);
                if(productFilter.isAllChecked) {
                    refPayload.current = {
                        ...refPayload.current, countryId: selectedCountry.id, productArray: "", applicationArray: "", currencyId, contractArray, startDate, endDate,
                        omittedClientIds: res.omittedClientIds, omittedIBCIds: res.omittedIBCIds,                    
                    };
                }
                else{
                    let productArray: string[] = [], applicationArray: number[] = [];
                    productFilter.selectedProducts.forEach((product: Product) => {
                        productArray.push(`'${product.name}'`);
                        product.applicationList.forEach((application: Application) => applicationArray.push(application.applicationID));
                    });
                    refPayload.current = {
                        ...refPayload.current, currencyId, contractArray, startDate, endDate,
                        countryId: selectedCountry.id,
                        productArray: productArray.join(','), applicationArray: applicationArray.join(','),
                        omittedClientIds: res.omittedClientIds, omittedIBCIds: res.omittedIBCIds,
                    };
                }
                queueRequest.current = selectedDealers;
                setProgress({ total: selectedDealers.length, current: 0 });
                startQueue();
            }else{
                throw new Error(res.errMsg);
            }
        }).catch((err) => {
            setPrepareMsg(err.message);
            setBtnSearch({ loading: false, disabled: false });
            setBtnExcel(prev => ({ ...prev, loading: false, disabled: false }));
        });
    }

    const onExcel = async() => {
        const {startDate, endDate} = refDateCard.current.getDateRange();
        const productFilter = refProductCard.current.getSelectedProducts();
        const contractArray = refDealerCard.current.getUnselectedContracts();
        const selectedDealers = refDealerCard.current.getSelectedDealers();
        const selectedClasses = refDealerCard.current.getSelectedClasses();
        const classList = refDealerCard.current.getAllClasses();
        const currencyId = currencyDropdownRef.current.getCurrency();
        const selectedCountry = refDealerCard.current.getSelectedCountry();
        if(!startDate || !endDate || productFilter.selectedProducts.length === 0 || selectedDealers.length === 0 || contractArray.length === 0) return;
        let strProducts: string[] = [], strApplications: string[] = [], strDealerClasses: string[] = [], strAccountableTypes: string[] = [];
        for(let product of productFilter.selectedProducts) {
            strProducts.push(product.name);
            for(let application of product.applicationList) strApplications.push(application.name);
        }
        for(let [dealerClassId, typeIdArray] of selectedClasses.entries()) {
            let dealerClass = classList.find((x: DealerClass) => x.dealerClassID === dealerClassId);
            if(dealerClass){
                strDealerClasses.push(`${dealerClass.name}-${dealerClass.description}`);
                strAccountableTypes = [...strAccountableTypes, ...dealerClass.typeList.filter((x: AccountableType) => typeIdArray.includes(x.accountableTypeID)).map((t: AccountableType) => `${dealerClass.name}, ${t.name} - ${t.description}`)];
            }
        }
        setBtnExcel(prev => ({ ...prev, loading: true, disabled: true }));
        setBtnSearch(prev => ({ ...prev, disabled: true }));
        setProgress({ total: 100, current: 0 });
        setPrepareMsg('Preparing excel data...');
        generateExcel({startDate, endDate, currencyId,
            countryId: selectedCountry.id, strCountry: selectedCountry.name, strDealerStatus: refDealerCard.current.getDealerStatus().label,
            strProducts: `All,${strProducts.join(',')}`,
            strApplications: `All,${strApplications.join(',')}`,
            strDealers: `All,${selectedDealers.map((dealer: Dealer) => `${dealer.dealerNumber} - ${dealer.name}`).join(',')}`,
            strDealerClasses: `Include Dealer with: no class,${strDealerClasses.join(',')}`,
            strAccountableTypes: `Include Dealer with: no type,${strAccountableTypes.join(',')}`,
            strContractTypes: contractArray.length == 0 ? '' : `Include clients with:no contract,${contractArray.join(',')}`,
            strInvoiceCalculationID: refIBCIdArray.current.join(',')
        }).unwrap().then((res) => {
            if(res.errMsg == 'OK') {
                setProgress(prev => ({ ...prev, current: prev.current + 20 }));
                setPrepareMsg('Prepare excel data done.');
                summarySheet(res.generationOccasionId);
                marketSheet(res.generationOccasionId);
                dealerSheet(res.generationOccasionId);
            }else{
                throw new Error(res.errMsg);
            }
        }).catch((err) => {
            setPrepareMsg(err.message);
            setBtnSearch(prev => ({ ...prev, disabled: false }));
            setBtnExcel(prev => ({ ...prev, loading: false, disabled: false }));
        });
    }
       
    return (
        <div style={{ padding: 20, gap: 20, display: 'grid' }}>
            <div style={{ border: "1px solid #E1E1E1", padding: 15, gap: 15, borderRadius: 8, backgroundColor: "#FFFFFF", display: "grid" }}>
                <div style={{ display: "flex", justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="heading4">Searching filters</Typography>
                    <div style={{ gap: 10, display: 'flex', alignItems: 'center' }}>
                        <Typography variant='subtitle2' style={{ color: '#575757' }}>Active filters</Typography>
                        <div style={{ width: 532 }}>
                            <Dropdown size='small'></Dropdown>
                        </div>
                        <div style={{ width: 182 }}>
                            
                        </div>
                    </div>
                </div>                
                <InlineMessage variant="warning" icon="alert">
                    GDS CPU data has not yet been loaded for invoicing of 202411
                </InlineMessage>
                <div style={{ gap: 15, display: 'flex' }}>
                    <div style={{ width: 400, gap: 15, display: 'grid', height: 772 }}>
                        <DateCard ref={refDateCard} />
                        <ProductCard ref={refProductCard} />
                    </div>
                    <DealerCard ref={refDealerCard} />
                </div>
                <div style={{ gap: 10, display: 'flex', justifyContent: 'flex-end', flexDirection: 'row', alignItems: 'center', flexFlow: 'row' }}>
                    <Typography variant='subtitle2' style={{ color: '#575757'}}>Output currency</Typography>
                    <CurrencyDropdown ref={currencyDropdownRef} />
                    <Button onClick={onSubmit} type='button' loading={btnSearch.loading} disabled={btnSearch.disabled} variant="primary" size='small' iconStart='view'>View application costs</Button>
                    <Button onClick={onExcel} type='button' loading={btnExcel.loading} disabled={btnExcel.disabled} variant='primary' size='small' iconStart='excel-document'>Excel</Button>
                </div>                
            </div>
            {progress.total !== undefined && (<div><Typography variant='subtitle1'>{progress.current} / {progress.total}</Typography><ProgressBar progress={(progress.current / progress.total) * 100} /></div>)}
            {prepareMsg !== undefined && (<div>{prepareMsg}</div>)}
            {requestList.length > 0 && requestList.map(() => (<div><Spinner /></div>))}
            {errorList.length > 0 && errorList.map((errMsg: string) => (<div>{errMsg}<Button variant="primary" size='small' iconEnd='refresh'>Retry</Button></div>))}
            {invoiceList.length > 0 && invoiceList.map((invoice: Invoice) => (
                <InvoiceTable key={`invoice-${invoice.dealerId}`} invoice={{ ...invoice }} />
            ))}
        </div>
    )
}